import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

const LEGACY_TOKEN_KEY = 'lem_access_token';

interface MinimalJwtPayload {
  exp?: number;
}

type SessionMessage = { type: 'clear' };

/**
 * Stocke le JWT d'accès uniquement en mémoire.
 *
 * Le refresh token reste dans un cookie HttpOnly et permet de reconstruire la
 * session après un rechargement. Le JWT n'est donc plus exposé au localStorage
 * en cas de XSS. Un BroadcastChannel propage uniquement les déconnexions entre
 * onglets, jamais le token lui-même.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private token: string | null = null;
  private readonly channel: BroadcastChannel | null;
  private readonly clearedSubject = new Subject<void>();
  readonly cleared$: Observable<void> = this.clearedSubject.asObservable();

  constructor() {
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch {
      // Le stockage peut être indisponible en navigation privée stricte.
    }

    this.channel = typeof BroadcastChannel === 'function'
      ? new BroadcastChannel('lem-auth-session')
      : null;

    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<SessionMessage>) => {
        if (event.data?.type === 'clear') {
          this.clearInternal(false);
        }
      };
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setToken(token: string): void {
    const normalized = token.trim();
    this.token = normalized || null;
  }

  clear(): void {
    this.clearInternal(true);
  }

  /** Retourne vrai uniquement lorsque l'expiration du JWT est connue et dépassée. */
  isExpired(token: string | null = this.getToken(), clockSkewSeconds = 5): boolean {
    if (!token) {
      return true;
    }

    const payload = this.decodePayload(token);
    if (!payload || typeof payload.exp !== 'number') {
      return true;
    }

    return payload.exp * 1000 <= Date.now() + clockSkewSeconds * 1000;
  }

  private clearInternal(broadcast: boolean): void {
    const hadToken = this.token !== null;
    this.token = null;
    if (broadcast) {
      this.channel?.postMessage({ type: 'clear' } satisfies SessionMessage);
    }
    if (hadToken) {
      this.clearedSubject.next();
    }
  }

  private decodePayload(token: string): MinimalJwtPayload | null {
    try {
      const [, encodedPayload] = token.split('.');
      if (!encodedPayload) {
        return null;
      }

      const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const json = decodeURIComponent(
        Array.from(atob(padded))
          .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join(''),
      );

      return JSON.parse(json) as MinimalJwtPayload;
    } catch {
      return null;
    }
  }
}
